/*

  e v e n t  l o g g i n g  i n t e r f a c e
  Event Logging Interface

  :description:
  A singleton class that acts as an interface for sending events to our pipelines.

*/

//
//  :code:
import { talk } from '../js/talk'

//
//  :packages:
import { EPL, ESCL, ESL } from '@eyekandy/event-logging'

//
//  :statics:
//  Variables and static declarations.
export const EYEKANDY_EVENTS_LOGGING_INTERFACE = 'EyekandyEventLoggingInterface'
const MODE_SYNDICATION = 'syndication'
const MODE_COVERAGE = 'coverage'
const MODE_PROJECTS = 'projects'
const MODE_CUSTOM = 'custom'

//
//  :classes:
export class EyekandyEventLoggingInterface {
  constructor (options) {
    if (typeof options === 'undefined') {
      options = {}
    }
    //
    //  :core:
    //  Core settings and values.
    this.name = 'eyekandy-event-logging-interface'
    this.version = 'v4.0.0'
    this.group = '[EYEKANDY-EVENT-LOGGING-INTERFACE]'

    //
    //  :runtime:
    //  At runtime we'll need running instances of the logging libraries.
    //  Let's not even create them until we've been signalled by a caller to do so, we only really need 1x of these
    //  per app/DOM anyways.
    this.libs = {
      syndication: null,
      coverage: null,
      projects: null,
      custom: null
    }
    //
    //  We need a way to control sending events, disable/enable and a flag to not write to pipeline.
    this.enabled = false
    this.canWriteToEventPipeline = true
    this.activeMode = null

    //
    //  :echo:
    //  Write out into the console that the instance was created.
    talk([this.group, `created a new instance @ '${this.version}'`])
  }

  //
  //  :api:
  //  Exposed API functions for callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  /**
   * Our top-level API function for external callers to use when the logging runtime has already been set up.
   * Ideally utilise this interface in external applications.
   * @param {Object} valuesToLog {type: "", ...}
   * @param {*} runtimeLoggingFunctionName
   */
  send (valuesToLog, runtimeLoggingFunctionName) {
    //
    //  :step 1:
    //  Verify that our active mode is valie
    if (!this.activeMode) {
      throw new Error(
        `${this.group} error, activeMode is not set, I don't know how to pass the event onto a logging library runtime`
      )
    }
    //
    //  :step 2:
    //  It might be that our logger was never initialised.
    if (!this.libs[this.activeMode]) {
      throw new Error(
        `${this.group} there is no runtime instance for the our active logging mode '${this.activeMode}', initialise it before using it using initialiseForMode()`
      )
    }
    //
    //  :step 3:
    //  We need to work out what arguments we want to send to our logging runtime.
    //  This approach is very opinionated as we're unpacking the "valuesToLog" give to us and passing them as fixed parameters to our logging
    //  runtimes. Custom modes can't be known, so we'll just collapse the given valuesToLog.
    const loggingFunctionArgumentsArray = []

    //
    //  Depending on our active mode, we may need to change the arguments we send to.
    const DEFAULT_VALUE = null
    if (this.activeMode === MODE_SYNDICATION) {
      loggingFunctionArgumentsArray.push(
        valuesToLog.type || DEFAULT_VALUE,
        valuesToLog.detail || DEFAULT_VALUE,
        valuesToLog.service || DEFAULT_VALUE,
        valuesToLog.arid || DEFAULT_VALUE
      )
    }
    if (this.activeMode === MODE_COVERAGE) {
      loggingFunctionArgumentsArray.push(
        valuesToLog.sku || DEFAULT_VALUE,
        valuesToLog.ean || DEFAULT_VALUE,
        valuesToLog.mpn || DEFAULT_VALUE,
        valuesToLog.name || DEFAULT_VALUE,
        valuesToLog.brand || DEFAULT_VALUE
      )
    }
    if (this.activeMode === MODE_PROJECTS) {
      loggingFunctionArgumentsArray.push(
        valuesToLog.type || DEFAULT_VALUE,
        valuesToLog.detail || DEFAULT_VALUE,
        valuesToLog.project || DEFAULT_VALUE,
        valuesToLog.arid || DEFAULT_VALUE
      )
    }
    if (this.activeMode === MODE_CUSTOM) {
      for (const [k, v] of Object.entries(valuesToLog)) {
        loggingFunctionArgumentsArray.push(v)
      }
    }
    //
    //  :step 4:
    //  Now we can actually call our logging function's send() function to send the event.
    this._send(
      this.activeMode,
      loggingFunctionArgumentsArray,
      runtimeLoggingFunctionName
    )
  }

  /**
   * Our wrapper function for sending syndication logs.
   * @param {String} type The event type to log.
   * @param {String} detail The detail key to log.
   * @param {String} service The service name to log.
   * @param {String} arid The ARID to log.
   * @returns {Boolean} True if the event was sent, else False.
   */
  sendSyndicationLog (type, detail, service, arid) {
    return this._send(MODE_SYNDICATION, {
      type: type,
      detail: detail,
      service: service,
      arid: arid
    })
  }

  /**
   * Our wrapper function for sending syndication coverage logs.
   * @param {String} sku The SKU of the product.
   * @param {String} ean The EAN of the product.
   * @param {String} mpn The MPN of the product.
   * @param {String} upc The UPC of the product.
   * @param {String} name The name of the product.
   * @param {String} brand The brand of the product.
   * @returns
   */
  sendCoverageLog (sku, ean, mpn, upc, name, brand) {
    return this._send(MODE_COVERAGE, {
      sku: sku,
      ean: ean,
      mpn: mpn,
      upc: upc,
      name: name,
      brand: brand
    })
  }

  /**
   * Our wrapper function for sending syndication logs.
   * @param {String} type The event type to log.
   * @param {String} detail The detail key to log.
   * @param {String} service The service name to log.
   * @param {String} arid The ARID to log.
   * @returns {Boolean} True if the event was sent, else False.
   */
  sendProjectsLog (type, detail, project, arid) {
    return this._send(MODE_PROJECTS, {
      type: type,
      detail: detail,
      project: project,
      arid: arid
    })
  }

  /**
   * Our wrapper function to interface with custom logging library runtimes.
   * @param {Object} valuesToLog {key: "value", ...}
   * @param {String} runtimeLoggingFunctionName An optional string to use as the name logging function of the library to call.
   * @returns {Boolean} True if the event was sent, else False (although it's custom so it may not always be true).
   */
  sendCustomLog (valuesToLog, runtimeLoggingFunctionName) {
    return this._send(MODE_CUSTOM, valuesToLog, runtimeLoggingFunctionName)
  }

  setActiveMode (mode) {
    if (!this._isValidMode(mode)) {
      throw new Error(
        `${
          this.group
        } error, the mode '${mode}' is not valid, must be in '${this._getKnownModes().join(
          ', '
        )}'`
      )
    }
    this.activeMode = mode
  }

  upsertToLibs (values) {
    this._getKnownModes().map(mode => {
      if (this.libs[mode]) {
        for (const [k, v] of Object.entries(values)) {
          this.libs[mode][k] = v
        }
      }
      return true
    })
  }

  initialiseForMode (mode, options, customClass) {
    if (typeof customClass === 'undefined') {
      customClass = null
    }
    const classMap = {
      syndication: ESL,
      coverage: ESCL,
      projects: EPL,
      custom: customClass
    }
    //
    //  Ok, now build an instance of our logging library runtime.
    this.libs[mode] = new classMap[mode](options)
    //
    //  Set our active mode to the one we just initialised, caution will unset existing modes.
    this.activeMode = mode
    //
    //  Handle upserting any dynamic values.
    if (options) {
      const lib = this.libs[mode]
      if (options.user) {
        lib._upsertToSelf(lib.userKey, options.user)
      }
      if (options.session) {
        lib._upsertToSelf(lib.sessionKey, options.session)
      }
    }
  }

  //
  //  :internal:
  //  Internal functions, shouldn't need to be called from outside of the class.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _send (mode, valuesToLog, runtimeLoggingFunctionName) {
    //
    //  :step 1:
    //  Validate the mode given to us.
    if (!this._getKnownModes().includes(mode)) {
      talk(
        [
          this.group,
          `error, unknown mode '${mode}', this event will not be sent: `,
          valuesToLog
        ],
        'error'
      )
      return false
    }
    //
    //  Our valuesToLog needs to be an array.
    if (typeof valuesToLog === 'undefined') {
      valuesToLog = []
    } else {
      if (!Array.isArray(valuesToLog)) {
        talk(
          [
            this.group,
            `error, valuesToLog is not an array, no log will be sent:`,
            valuesToLog
          ],
          'error'
        )
        return false
      }
    }

    //
    //  :step 2:
    //  If we can send the event, let's do so.
    if (this._canSendEvent()) {
      talk([
        this.group,
        `sending a new event to the event pipeline:`,
        valuesToLog
      ])
      //
      //  :step 2a:
      //  We need to get a handle on our library and send this event.
      if (runtimeLoggingFunctionName) {
        if (typeof this.libs[mode][runtimeLoggingFunctionName] !== 'function') {
          talk(
            [
              this.group,
              `error, the logging function name given '${runtimeLoggingFunctionName}' is not valid for the runtime logging`,
              `library: '${this.libs[mode][runtimeLoggingFunctionName]}'`,
              valuesToLog
            ],
            'error'
          )
          return false
        }
        this.libs[mode][runtimeLoggingFunctionName](...valuesToLog)
      } else {
        this.libs[mode].log(...valuesToLog)
      }

      //
      //  TODO: Change the content type in the response to txt in the backend.
      //
      return true
    }

    //
    //  :step 3:
    //  We did not send the event, echo it out instead.
    talk(
      [
        this.group,
        `** WARNING: an event was not sent to the event pipeline:`,
        valuesToLog,
        `enabled: '${this.enabled}', canWriteToEventPipeline: '${this.canWriteToEventPipeline}' **`
      ],
      'warning'
    )
    return false
  }

  _canSendEvent () {
    return this.enabled && this.canWriteToEventPipeline
  }

  _enable () {
    this.enabled = true
  }
  _disable () {
    this.enabled = false
  }
  _setCanWriteToEventPipeline () {
    this.canWriteToEventPipeline = true
  }
  _setCannotWriteToEventPipeline () {
    this.canWriteToEventPipeline = false
  }

  _getKnownModes () {
    return Array.from(Object.keys(this.libs))
  }

  _isValidMode (mode) {
    return this._getKnownModes().includes(mode)
  }
}
